import { ChangeDetectorRef, Directive, ElementRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DsButtonVariants, DsModalComponent, DsModalService, DsModalVariant, Icons } from '@levelaccess/design-system';
import { Subscription } from 'rxjs';

import { IAutomatedScanIssue, IScanIssues } from '@shared/interfaces/scan-issues.interface';
import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import { $flaw, changedByType, flawStatus } from '@shared/constants/flaw';
import { IFlawStatusChange } from '@shared/interfaces/flaw.interface';
import { $scanIssues } from '@shared/constants/scan-issues';
import { RuleAuditHistoryAction } from '@shared/constants/rule-audit-history';
import { ITask } from '@shared/interfaces/task.interface';
import { $task } from '@shared/constants/task';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { IIssueActionContext, IssueActionsService } from '@client/app/services/issue-finding-details/issue-actions.service';
import { TranslateService } from '@client/app/translate/translate.service';
import { ScanReportService } from '@client/app/services/scan-report.service';
import { NotificationPosition } from '@client/app/models/notification.model';
import { NotificationService } from '@client/app/services/notification.service';
import { LoadErrorHandler } from '@client/app/services/load-error-handler';
import { ShareIssueReportModalComponent } from '@client/app/components/share-issue-report-modal/share-issue-report-modal.component';
import { A11yService } from '@client/app/services/a11y.service';
import { CommonUtility } from '@client/app/utility/common.utility';
import { UserService } from '@client/app/services/user.service';
import {
  FindingNavigationDirection,
  IFindingModalNavigation,
  IIssuesViewDetailsModalCloseResult,
} from '@client/app/pages/public/scans/scan-results/issues-view-details-base/issues-view-details-modal.interface';

@Directive()
export abstract class IssuesViewDetailsModalBaseComponent implements OnDestroy {
  @Input() public issue: IAutomatedScanIssue;
  @Input() public ruleDetails: IScanIssues;
  @Input() public scanId: string;
  @Input() public navigation?: IFindingModalNavigation;

  @ViewChild('modalRef') private dsModal?: DsModalComponent;

  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;

  protected subscriptions: Subscription = new Subscription();
  protected createdTask?: ITask;
  protected statusChanged: boolean = false;

  protected abstract get actionContext(): IIssueActionContext;
  protected abstract get shareToolName(): AccessibilityAuditToolNames;

  protected constructor(
    public modal: NgbActiveModal,
    protected translateService: TranslateService,
    protected scanReportService: ScanReportService,
    protected dsModalService: DsModalService,
    protected notificationService: NotificationService,
    protected loadErrorHandler: LoadErrorHandler,
    protected issueActionsService: IssueActionsService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected elementRef: ElementRef<HTMLElement>,
    protected a11yService: A11yService,
    protected userService: UserService,
  ) {}

  public get modalTitle(): string {
    return this.translateService.instant('findings_details_for_id', this.issue?.[$flaw.issueId]);
  }

  public updateFinding(issue: IAutomatedScanIssue, direction: FindingNavigationDirection): void {
    this.issue = issue;
    this.changeDetectorRef.detectChanges();
    this.focusModalTitle();
    this.announceFindingNavigation(direction);
  }

  public focusModalTitle(): void {
    const titleId: string | undefined = this.dsModal?.titleId;
    const titleElement: HTMLElement | null = titleId
      ? document.getElementById(titleId)
      : this.elementRef.nativeElement.querySelector('.modal-title');
    if (SharedCommonUtility.isNullish(titleElement)) {
      return;
    }
    if (!titleElement.hasAttribute('tabindex')) {
      titleElement.setAttribute('tabindex', '-1');
    }
    CommonUtility.setFocusToElement(null, titleElement);
  }

  public announceFindingNavigation(direction: FindingNavigationDirection): void {
    const translationKey: string =
      direction === 'previous' ? 'finding_navigation_previous_loaded' : 'finding_navigation_next_loaded';
    const message: string = this.translateService.instant(translationKey, [
      this.issue?.[$flaw.issueId] ?? '',
      this.issue?.data?.actualResult ?? '',
    ]);
    this.a11yService.setMessage(message);
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (SharedCommonUtility.isNullish(this.navigation)) {
      return;
    }

    const target: EventTarget | null = event.target;
    if (this.isEditableTarget(target)) {
      return;
    }

    if (event.key === 'ArrowLeft' && this.navigation.canGoPrevious) {
      event.preventDefault();
      this.navigation.goToPrevious();
      return;
    }

    if (event.key === 'ArrowRight' && this.navigation.canGoNext) {
      event.preventDefault();
      this.navigation.goToNext();
    }
  }

  public onDismissFinding(): void {
    this.dismissOrRestoreFinding(
      RuleAuditHistoryAction.dismiss,
      'mark_error_as_false_positive_success',
      'mark_error_as_false_positive_error',
    );
  }

  public onRestoreFinding(): void {
    this.dismissOrRestoreFinding(
      RuleAuditHistoryAction.restore,
      'unmark_error_as_false_positive_success',
      'unmark_error_as_false_positive_error',
    );
  }

  public onCreateTask(): void {
    this.subscriptions.add(
      this.issueActionsService.openCreateTaskSlideout([this.issue], this.actionContext).subscribe({
        next: (task: ITask): void => {
          this.createdTask = task;
          this.issue.task = {
            [$task._id]: task[$task._id],
            [$task.projectId]: task[$task.projectId],
            [$task.taskProjectId]: task[$task.taskProjectId],
          };
        },
      }),
    );
  }

  public onModalClosed(): void {
    this.modal.close({
      reloadNeeded: this.statusChanged || SharedCommonUtility.notNullish(this.createdTask),
      task: this.createdTask,
    } satisfies IIssuesViewDetailsModalCloseResult);
  }

  public readonly beforeClose = (): boolean => {
    this.onModalClosed();
    return true;
  };

  public async openShareModal(): Promise<void> {
    const issueLink: string = await this.scanReportService
      .getIssueShortLink(this.scanId, this.shareToolName, this.ruleDetails[$scanIssues.ruleId], this.issue.rawFindingId)
      .catch((): string => window.location.href);

    const modalInstance: ShareIssueReportModalComponent = this.dsModalService.open(
      ShareIssueReportModalComponent,
      DsModalVariant.center,
    ).componentInstance;
    modalInstance.modalInputData = { data: { url: issueLink } };
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private dismissOrRestoreFinding(actionType: RuleAuditHistoryAction, successKey: string, errorKey: string): void {
    this.subscriptions.add(
      this.issueActionsService.openDismissOrRestoreModal([this.issue], actionType, this.actionContext).subscribe({
        next: (): void => {
          this.notificationService.success(this.translateService.instant(successKey), NotificationPosition.Toast);
          this.statusChanged = true;
          const newStatus: flawStatus = actionType === RuleAuditHistoryAction.dismiss ? flawStatus.dismissed : flawStatus.open;
          const statusChange: IFlawStatusChange = {
            newStatus,
            date: new Date(),
            changedByType: changedByType.user,
            changedBy: this.userService.getStoredUserId(),
            comment: '',
          };
          this.issue = {
            ...this.issue,
            [$flaw.status]: newStatus,
            [$flaw.statusHistory]: [...(this.issue[$flaw.statusHistory] ?? []), statusChange],
          };
          this.changeDetectorRef.detectChanges();
        },
        error: (err: HttpErrorResponse): void => {
          this.loadErrorHandler.handleError(errorKey, err, this.constructor.name);
        },
      }),
    );
  }

  private isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const tagName: string = target.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      return true;
    }

    return target.isContentEditable;
  }
}
